import { create } from 'zustand';

const STORAGE_KEY = 'fnbCustomer.cartRecovery.v2';
const LEGACY_STORAGE_KEY = 'fnbCustomer.cartRecovery.v1';
const SCHEMA_VERSION = 2;

export function isFutureCartExpiry(value?: string | null): value is string {
  if (!value) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && timestamp > Date.now();
}

export function earliestCartExpiry(
  cartExpiresAt?: string | null,
  grantExpiresAt?: string | null
): string | null {
  if (!isFutureCartExpiry(cartExpiresAt) || !isFutureCartExpiry(grantExpiresAt)) return null;
  return Date.parse(cartExpiresAt) <= Date.parse(grantExpiresAt) ? cartExpiresAt : grantExpiresAt;
}

/** Runtime-only Cart access. Cart identity and proof never enter localStorage. */
export interface CartRecovery {
  cartId: string;
  cartProof: string;
  recoveryGrant: string;
  storeCode: string;
  expiresAt: string;
}

export interface PersistedCartRecovery {
  schemaVersion: number;
  recoveryGrant: string;
  storeCode: string;
  expiresAt: string;
}

interface CartWorkflowState {
  recovery: CartRecovery | null;
  pendingRecovery: PersistedCartRecovery | null;
  recoveryStatus: 'idle' | 'recovering' | 'failed';
  drawerOpen: boolean;
  pendingItemId: string | null;
  selectedMood: { moodDefinitionId: string; requestedLocale: string } | null;
  setRecovery: (recovery: CartRecovery) => void;
  rotateRecoveryGrant: (
    grant: string | undefined,
    grantExpiresAt: string | undefined,
    cartExpiresAt?: string
  ) => void;
  consumePendingRecovery: () => PersistedCartRecovery | null;
  markRecoveryFailed: () => void;
  clearRecovery: () => void;
  setDrawerOpen: (open: boolean) => void;
  setPendingItemId: (id: string | null) => void;
  setSelectedMood: (
    mood: { moodDefinitionId: string; requestedLocale: string } | null
  ) => void;
}

function browserStorage(kind: 'localStorage' | 'sessionStorage'): Storage | null {
  if (typeof window === 'undefined') return null;
  const storage = window[kind];
  return storage && typeof storage.getItem === 'function' && typeof storage.setItem === 'function'
    ? storage
    : null;
}

function removePersistedRecovery() {
  browserStorage('localStorage')?.removeItem(STORAGE_KEY);
}

function readPersistedRecovery(): PersistedCartRecovery | null {
  const local = browserStorage('localStorage');
  const session = browserStorage('sessionStorage');
  // The old record contained Cart id and Cart proof. It is deliberately discarded, never migrated.
  session?.removeItem(LEGACY_STORAGE_KEY);
  if (!local) return null;
  try {
    const value = JSON.parse(local.getItem(STORAGE_KEY) ?? 'null') as PersistedCartRecovery | null;
    if (
      value?.schemaVersion !== SCHEMA_VERSION ||
      !value.recoveryGrant ||
      !value.storeCode ||
      !isFutureCartExpiry(value.expiresAt)
    ) {
      removePersistedRecovery();
      return null;
    }
    return value;
  } catch {
    removePersistedRecovery();
    return null;
  }
}

function persist(recovery: Pick<CartRecovery, 'recoveryGrant' | 'storeCode' | 'expiresAt'>) {
  browserStorage('localStorage')?.setItem(
    STORAGE_KEY,
    JSON.stringify({
      schemaVersion: SCHEMA_VERSION,
      recoveryGrant: recovery.recoveryGrant,
      storeCode: recovery.storeCode,
      expiresAt: recovery.expiresAt,
    } satisfies PersistedCartRecovery)
  );
}

const restored = readPersistedRecovery();

export const useCartWorkflowStore = create<CartWorkflowState>((set, get) => ({
  recovery: null,
  pendingRecovery: restored,
  recoveryStatus: 'idle',
  drawerOpen: false,
  pendingItemId: null,
  selectedMood: null,
  setRecovery: (recovery) => {
    if (!isFutureCartExpiry(recovery.expiresAt)) {
      removePersistedRecovery();
      set({ recovery: null, pendingRecovery: null, recoveryStatus: 'failed' });
      return;
    }
    persist(recovery);
    set({ recovery, pendingRecovery: null, recoveryStatus: 'idle' });
  },
  rotateRecoveryGrant: (grant, grantExpiresAt, cartExpiresAt) => {
    const recovery = get().recovery;
    if (!recovery || !grant || !grantExpiresAt) return;
    const expiresAt = cartExpiresAt
      ? earliestCartExpiry(cartExpiresAt, grantExpiresAt)
      : isFutureCartExpiry(grantExpiresAt)
        ? grantExpiresAt
        : null;
    if (!expiresAt) {
      get().clearRecovery();
      return;
    }
    const next = { ...recovery, recoveryGrant: grant, expiresAt };
    persist(next);
    set({ recovery: next });
  },
  consumePendingRecovery: () => {
    const pendingRecovery = get().pendingRecovery;
    if (!pendingRecovery) return null;
    if (!isFutureCartExpiry(pendingRecovery.expiresAt)) {
      removePersistedRecovery();
      set({ pendingRecovery: null, recoveryStatus: 'failed' });
      return null;
    }
    set({ pendingRecovery: null, recoveryStatus: 'recovering' });
    return pendingRecovery;
  },
  markRecoveryFailed: () => {
    removePersistedRecovery();
    set({ recovery: null, pendingRecovery: null, recoveryStatus: 'failed' });
  },
  clearRecovery: () => {
    removePersistedRecovery();
    browserStorage('sessionStorage')?.removeItem(LEGACY_STORAGE_KEY);
    set({
      recovery: null,
      pendingRecovery: null,
      recoveryStatus: 'idle',
      drawerOpen: false,
      pendingItemId: null,
    });
  },
  setDrawerOpen: (drawerOpen) => set({ drawerOpen }),
  setPendingItemId: (pendingItemId) => set({ pendingItemId }),
  setSelectedMood: (selectedMood) => set({ selectedMood }),
}));

export const cartRecoveryStorageKey = STORAGE_KEY;
export const legacyCartRecoveryStorageKey = LEGACY_STORAGE_KEY;
